// File: src/pages/showingbooker/components/StripePayment.js
import React, { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements
} from '@stripe/react-stripe-js';
import styled from 'styled-components';
import NotificationService from '../../../services/notificationService';
import { showingbookerAPI } from '../../../services/showingbookerAPI';

// Use live key for accepting real payments
const STRIPE_PUBLISHABLE_KEY = 'pk_live_51RbRBSBAaKP5ns2yV3rvILmpTSZKOtlC5OCDyizbppj9jVlEPwmw0RlwDyOS5gZrniI7W12oVH44jhAlp3QCAw8h00yt6yMcFx';

console.log('🔧 Stripe publishable key:', STRIPE_PUBLISHABLE_KEY ? `SET (${STRIPE_PUBLISHABLE_KEY.substring(0, 20)}...)` : 'NOT SET');
console.log('🔧 Environment:', process.env.REACT_APP_ENVIRONMENT || 'development');
console.log('🔧 API URL:', process.env.REACT_APP_API_URL);

if (!STRIPE_PUBLISHABLE_KEY) {
  console.error('❌ REACT_APP_STRIPE_PUBLISHABLE_KEY is not set in environment variables');
}

const stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY);

const PaymentSection = styled.div`
  background: #f8f9fa;
  padding: 20px;
  border-radius: 8px;
  border: 1px solid #e9ecef;
  margin: 16px 0;
  
  @media (min-width: 768px) {
    padding: 25px;
    margin: 20px 0;
  }
`;

const PaymentForm = styled.form`
  display: grid;
  gap: 16px;
  
  @media (min-width: 768px) {
    gap: 20px;
  }
`;

const PaymentElementContainer = styled.div`
  padding: 16px;
  border: 1px solid #ddd;
  border-radius: 8px;
  background: white;
  margin: 12px 0;
  
  @media (min-width: 768px) {
    padding: 15px;
    border-radius: 4px;
    margin: 15px 0;
  }
`;

const PaymentButton = styled.button`
  width: 100%;
  padding: 18px;
  background: ${props => props.disabled ? '#6c757d' : '#6c5ce7'};
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 18px;
  font-weight: 600;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.3s ease;
  
  &:hover:not(:disabled) {
    background: #5f4fc5;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(108, 92, 231, 0.3);
  }
  
  @media (min-width: 768px) {
    padding: 16px;
    font-size: 16px;
    border-radius: 4px;
  }
`;

const ErrorMessage = styled.div`
  color: #dc3545;
  font-size: 14px;
  margin-top: 8px;
  padding: 10px;
  background: #f8d7da;
  border: 1px solid #f5c6cb;
  border-radius: 4px;
`;

const InfoText = styled.p`
  color: #6c757d;
  font-size: 14px;
  text-align: center;
  margin: 8px 0;
`;

// Inner component that uses Stripe hooks
const CheckoutForm = ({ amount, bookingData, propertyData, onPaymentSuccess, clientSecret }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = useState(null);
  const [processing, setProcessing] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    setProcessing(true);
    setError(null);

    try {
      // Check if this is a mock payment (development mode)
      const isMockPayment = clientSecret?.includes('mock');
      
      if (isMockPayment) {
        console.log('🧪 Processing mock payment for development...');
        
        // Simulate payment success for mock payments
        const mockPaymentIntentId = clientSecret.split('_secret_')[0];
        
        // Process the booking with the backend using mock payment ID
        try {
          const formattedBookingData = {
            ...bookingData,
            showing_date: bookingData.showingDate,
            showing_time: bookingData.showingTime,
            property_id: propertyData.id,
            requester_type: bookingData.requesterType,
            name: bookingData.name,
            email: bookingData.email,
            phone: bookingData.phone
          };
          const result = await showingbookerAPI.processBooking(
            mockPaymentIntentId,
            formattedBookingData,
            propertyData
          );
          
          console.log('🏠 Mock booking processing result:', result);
          
          if (result.success) {
            // Send notifications
            console.log('🔔 Triggering notifications for mock booking:', result.booking.confirmation_number);
            NotificationService.sendBookingNotifications(result.booking, propertyData)
              .then(results => {
                console.log('✅ Mock notification results:', results);
              })
              .catch(error => {
                console.error('❌ Mock notification error:', error);
              });
            
            onPaymentSuccess({
              paymentIntentId: mockPaymentIntentId,
              amount: amount,
              status: 'succeeded',
              booking: result.booking,
              mock: true
            });
          } else {
            throw new Error(result.error || 'Mock booking processing failed');
          }
        } catch (bookingError) {
          console.error('❌ Mock booking processing error:', bookingError);
          setError(`Mock payment succeeded but booking failed: ${bookingError.message}. Payment ID: ${mockPaymentIntentId}`);
        }
        
        setProcessing(false);
        return;
      }

      // Real Stripe payment processing
      if (!stripe || !elements || !clientSecret) {
        console.log('⚠️ Missing required payment components:', { stripe: !!stripe, elements: !!elements, clientSecret: !!clientSecret });
        setProcessing(false);
        return;
      }

      console.log('💳 Submitting payment elements...');
      
      // First, submit the elements (required by new Stripe API)
      const { error: submitError } = await elements.submit();
      if (submitError) {
        setError(submitError.message);
        setProcessing(false);
        return;
      }
      
      console.log('💳 Confirming payment with Stripe...');
      // Then confirm the payment with Stripe
      const { error: stripeError, paymentIntent } = await stripe.confirmPayment({
        elements,
        clientSecret, // Use the clientSecret passed from parent
        confirmParams: {
          // Return URL is required but we'll handle success before redirect
          return_url: window.location.href,
          payment_method_data: {
            billing_details: {
              email: bookingData.email,
              phone: bookingData.phone,
              name: bookingData.name
            }
          }
        },
        redirect: 'if_required' // Don't redirect automatically
      });

      if (stripeError) {
        setError(stripeError.message);
        setProcessing(false);
        return;
      }

      if (paymentIntent.status === 'succeeded') {
        console.log('✅ Payment succeeded, processing booking...');
        // Process the booking with the backend
        try {
          const formattedBookingData = {
            ...bookingData,
            showing_date: bookingData.showingDate,
            showing_time: bookingData.showingTime,
            property_id: propertyData.id,
            requester_type: bookingData.requesterType,
            name: bookingData.name,
            email: bookingData.email,
            phone: bookingData.phone
          };
          const result = await showingbookerAPI.processBooking(
            paymentIntent.id,
            formattedBookingData,
            propertyData
          );
          
          console.log('🏠 Booking processing result:', result);
          
          if (result.success) {
            // Send notifications
            console.log('🔔 Triggering notifications for booking:', result.booking.confirmation_number);
            NotificationService.sendBookingNotifications(result.booking, propertyData)
              .then(results => {
                console.log('✅ Notification results:', results);
              })
              .catch(error => {
                console.error('❌ Notification error:', error);
              });
            
            onPaymentSuccess({
              paymentIntentId: paymentIntent.id,
              amount: amount,
              status: 'succeeded',
              booking: result.booking
            });
          } else {
            throw new Error(result.error || 'Booking processing failed');
          }
        } catch (bookingError) {
          console.error('❌ Booking processing error:', bookingError);
          setError(`Payment succeeded but booking failed: ${bookingError.message}. Please contact support with payment intent ID: ${paymentIntent.id}`);
        }
      }
    } catch (err) {
      console.error('Payment error:', err);
      setError(err.message || 'An unexpected error occurred');
    }

    setProcessing(false);
  };

  return (
    <PaymentForm onSubmit={handleSubmit}>
      <PaymentElementContainer>
        {clientSecret && (
          <PaymentElement 
            options={{
              layout: 'tabs',
              fields: {
                billingDetails: {
                  email: 'never',
                  phone: 'never'
                }
              }
            }}
          />
        )}
      </PaymentElementContainer>
      
      {error && (
        <ErrorMessage>{error}</ErrorMessage>
      )}
      
      <PaymentButton 
        type="submit" 
        disabled={processing || !stripe || !elements}
      >
        {processing ? 'Processing...' : `Pay $${amount}`}
      </PaymentButton>
      
      <InfoText>
        🔒 Secure payment powered by Stripe
      </InfoText>
    </PaymentForm>
  );
};

// Main component that wraps with Elements provider
const StripePayment = ({ amount, bookingData, propertyData, onPaymentSuccess, onPaymentError }) => {
  const [clientSecret, setClientSecret] = useState('');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Only create PaymentIntent if we have valid booking data
    // For buyer's agent: need propertyId, buyerName, buyerAgentEmail
    // For direct buyer: need propertyId, buyerName, buyerEmail
    const hasRequiredData = bookingData && bookingData.propertyId && bookingData.name && bookingData.email;
    
    if (!hasRequiredData) {
      console.log('⏳ Waiting for complete booking data before creating payment intent...');
      console.log('📋 Current booking data:', bookingData);
      console.log('📋 Requester type:', bookingData?.requesterType);
      console.log('📋 Required email field:', bookingData?.requesterType === 'buyers_agent' ? 'buyerAgentEmail' : 'buyerEmail');
      console.log('📋 Email value:', bookingData?.requesterType === 'buyers_agent' ? bookingData?.buyerAgentEmail : bookingData?.buyerEmail);
      setLoading(false);
      return;
    }

    const createPaymentIntent = async () => {
      try {
        setLoading(true);
        setError(null);
        console.log('💳 Creating payment intent for Elements provider...', { amount, bookingData });
        
        // Normalize booking data for backend - ensure buyerEmail is always present
        const normalizedBookingData = {
          ...bookingData,
          email: bookingData.email,
          phone: bookingData.phone
        };
        console.log('💳 Normalized booking data for backend:', normalizedBookingData);
        
        const data = await showingbookerAPI.createPaymentIntent(amount, normalizedBookingData);
        setClientSecret(data.clientSecret);
        console.log('💳 Payment intent created for Elements:', data.paymentIntentId);
        
        // Check if this is a mock payment for development
        if (data.mock) {
          console.log('🧪 Mock payment detected:', data.warning);
        }
        
        setLoading(false);
        
      } catch (error) {
        console.error('❌ Error creating payment intent:', error);
        const errorMessage = `Failed to initialize payment: ${error.message}`;
        setError(errorMessage);
        setLoading(false);
        if (onPaymentError) {
          onPaymentError(errorMessage);
        }
      }
    };

    createPaymentIntent();
  }, [amount, bookingData]);

  const appearance = {
    theme: 'stripe',
    variables: {
      colorPrimary: '#6c5ce7',
      colorBackground: '#ffffff',
      colorText: '#30313d',
      colorDanger: '#df1b41',
      fontFamily: 'system-ui, sans-serif',
      borderRadius: '4px',
    },
  };

  const options = {
    clientSecret,
    appearance,
  };

  return (
    <PaymentSection>
      <h3>Payment Information</h3>
      <p>Complete your booking with a secure payment of ${amount}</p>
      
      {loading && (
        <InfoText>🔄 Initializing payment form...</InfoText>
      )}
      
      {error && (
        <ErrorMessage>{error}</ErrorMessage>
      )}
      
      {!STRIPE_PUBLISHABLE_KEY && (
        <ErrorMessage>⚠️ Payment system configuration error. Please contact support.</ErrorMessage>
      )}
      
      {clientSecret && !loading && !error && STRIPE_PUBLISHABLE_KEY && (
        <>
          {clientSecret.includes('mock') && (
            <div style={{
              background: '#fff3cd',
              border: '1px solid #ffeaa7',
              borderRadius: '4px',
              padding: '12px',
              margin: '12px 0',
              color: '#856404'
            }}>
              🧪 <strong>Development Mode:</strong> Using mock payment system. No real charges will be made.
            </div>
          )}
          <Elements options={options} stripe={stripePromise}>
            <CheckoutForm 
              amount={amount}
              bookingData={bookingData}
              propertyData={propertyData}
              onPaymentSuccess={onPaymentSuccess}
              clientSecret={clientSecret}
            />
          </Elements>
        </>
      )}
      
      {!loading && !error && !clientSecret && (
        <InfoText>⏳ Please complete all booking information above to proceed with payment.</InfoText>
      )}
    </PaymentSection>
  );
};

export default StripePayment;